import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * One-line post description for feed cards. Renders the body on a single line,
 * truncated to the card's width on that device, and appends a lighter gray
 * "more" only when the text actually overflows. No click handler is needed —
 * tapping anywhere on the card already opens the post.
 *
 * This visually replaces the old tags row; the tags themselves are still kept
 * in the post data so search/filtering keeps working.
 */
const CardDescription: React.FC<{ text: string }> = ({ text }) => {
	const textRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useLayoutEffect(() => {
		const el = textRef.current;
		if (!el) return;
		const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
		measure();
		// Re-check when the card width changes (responsive / sidebar toggles).
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [text]);

	return (
		<div className='flex items-baseline min-w-0'>
			<span
				ref={textRef}
				className='flex-1 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis text-left text-[11px] sm:text-sm text-gray-500 font-normal'
			>
				{text}
			</span>
			{isTruncated && (
				<span className='shrink-0 whitespace-nowrap text-[11px] sm:text-sm text-gray-500 font-semibold'>
					more
				</span>
			)}
		</div>
	);
};

export default CardDescription;
